import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatDrawerMode } from '@angular/material/sidenav';
import { ActivatedRoute } from '@angular/router';
import { DateTime } from 'luxon';
import {
  distinctUntilChanged,
  firstValueFrom,
  map,
  Observable,
  Subscription
} from 'rxjs';
import {
  AutoAssignResponse,
  DcScheduleAutoAssignFilters,
  DcScheduleDetails,
  DcScheduleFilters,
  DcScheduleRow,
  ScheduleAssignmentStatus,
  ScheduleAutoAssignStatus,
  TeamLeaderAssignPreview,
  TeamLeaderAssignResult,
  TeamLeaderCoverageRow,
  TeamLeaderOption
} from '../domain/dc-schedule.model';
import {
  deriveEffectiveScheduleAutoAssignStatus,
  deriveScheduleAssignmentStatus
} from '../domain/dc-schedule-status.util';
import { GameStatus } from '../domain/game';
import { AuthService } from '../auth/auth.service';
import {
  ScheduleAutoAssignDialogComponent,
  ScheduleAutoAssignDialogData,
  ScheduleAutoAssignDialogItem
} from './schedule-auto-assign-dialog/schedule-auto-assign-dialog.component';
import { ScheduleAutoAssignResultDialogComponent } from './schedule-auto-assign-result-dialog/schedule-auto-assign-result-dialog.component';
import {
  ScheduleTeamLeaderAssignDialogComponent,
  ScheduleTeamLeaderAssignDialogData,
  ScheduleTeamLeaderCoveragePlanItem
} from './schedule-team-leader-assign-dialog/schedule-team-leader-assign-dialog.component';
import {
  TeamLeaderAssignmentConfigDialogComponent,
  TeamLeaderAssignmentConfigDialogData
} from './team-leader-assignment-config-dialog/team-leader-assignment-config-dialog.component';
import { AlertService } from '../services/alert.service';
import {
  DcRosterService,
  TASKS_SOURCE_TIME_ZONE
} from '../services/dc-roster.service';
import { getHttpErrorMessage } from '../shared/http-error-message.util';
import { scheduleProgressTooltip } from './schedule-progress.util';

interface ScheduleFilters {
  dcType: string;
  league: string;
  gameDate: string;
  dcDateFrom: string;
  dcDateTo: string;
  teamLeaderAuthId: string;
  gameStatus: GameStatus | '';
}

const EMPTY_FILTERS: ScheduleFilters = {
  dcType: '',
  league: '',
  gameDate: '',
  dcDateFrom: '',
  dcDateTo: '',
  teamLeaderAuthId: '',
  gameStatus: ''
};
const INITIAL_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = [10, 20, 50];
const DEFAULT_DC_DATE_RANGE_DAYS = 7;
const UNASSIGNED_TEAM_LEADER_FILTER_VALUE = '__unassigned__';
const MISSING_GM_EVENT_ERROR_CODE = 'dc_roster_missing_gm_event';
const OBJECT_ID_HEX_PATTERN = /^[0-9a-fA-F]{24}$/;
const MISSING_GM_EVENT_TEAM_LEADER_MESSAGE =
  'This game cannot have a Team Leader assigned because its DC Roster does not contain a GM event. Add the GM event to the DC Roster and try again.';

@Component({
  selector: 'app-schedule',
  templateUrl: './schedule.component.html',
  styleUrls: [
    '../shared/table-row-hover.shared.css',
    './schedule.component.css'
  ]
})
export class ScheduleComponent implements OnInit, OnDestroy {
  readonly displayedColumns = [
    'select',
    'collectionStatus',
    'progress',
    'dcType',
    'league',
    'gameDate',
    'dcDatetime',
    'etcDatetime',
    'teams',
    'teamLeader',
    'autoAssignStatus',
    'roster',
    'expand'
  ];
  readonly detailRowColumns = ['expandedDetail'];
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;
  readonly gameStatusOptions = Object.values(GameStatus);
  readonly unassignedTeamLeaderFilterValue =
    UNASSIGNED_TEAM_LEADER_FILTER_VALUE;

  rows: DcScheduleRow[] = [];
  dcTypeOptions: string[] = [];
  leagueOptions: string[] = [];
  teamLeaders: TeamLeaderOption[] = [];
  inactiveAssignedTeamLeaders: Pick<TeamLeaderOption, 'auth0Id' | 'name'>[] =
    [];
  filters: ScheduleFilters = this.createDefaultFilters();
  selectedGameIds = new Set<string>();
  expandedGameIds = new Set<string>();
  savingTeamLeaderGameIds = new Set<string>();
  markingAssignedGameId: string | null = null;
  hasWritePermission = false;
  loading = false;
  running = false;
  totalCount = 0;
  autoAssignGameLimit: number | null = null;
  pageIndex = 0;
  pageSize = INITIAL_PAGE_SIZE;
  availabilityActivated = false;
  availabilityOpened = false;
  availabilityFullscreen = false;
  availabilityDrawerMode: MatDrawerMode = 'side';
  requestedGameId: string | null = null;
  focusedGameId: string | null = null;
  focusedGameErrorMessage = '';

  private destroyed = false;
  private scheduleLoadVersion = 0;
  private teamLeaderDirectory = new Map<string, string>();
  private routeParamSubscription: Subscription | null = null;

  constructor(
    private dcRosterService: DcRosterService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private authService: AuthService,
    private route: ActivatedRoute
  ) {}

  get selectedCount(): number {
    return this.selectedGameIds.size;
  }

  get allFilteredSelected(): boolean {
    return (
      this.rows.length > 0 &&
      this.rows.every((row) => this.selectedGameIds.has(row.gameId))
    );
  }

  async ngOnInit() {
    this.updateAvailabilityDrawerMode();
    let initialScheduleLoad: Promise<void> | null = null;
    this.routeParamSubscription = this.route.paramMap
      .pipe(
        map((paramMap) => paramMap.get('gameId')?.trim() ?? ''),
        distinctUntilChanged()
      )
      .subscribe((gameId) => {
        const scheduleLoad = this.loadFocusedGame(gameId);
        if (!initialScheduleLoad) {
          initialScheduleLoad = scheduleLoad;
          return;
        }
        void scheduleLoad;
      });
    const initialLoads = [this.loadWritePermission(), this.loadTeamLeaders()];
    if (initialScheduleLoad) {
      initialLoads.push(initialScheduleLoad);
    }
    await Promise.all(initialLoads);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.routeParamSubscription?.unsubscribe();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateAvailabilityDrawerMode();
  }

  openAvailability(): void {
    this.availabilityActivated = true;
    this.availabilityOpened = true;
  }

  closeAvailability(): void {
    this.availabilityOpened = false;
    this.availabilityFullscreen = false;
  }

  onAvailabilityFullscreenChange(fullscreen: boolean): void {
    this.availabilityFullscreen = fullscreen;
  }

  private updateAvailabilityDrawerMode(): void {
    this.availabilityDrawerMode =
      typeof window !== 'undefined' && window.innerWidth <= 900
        ? 'over'
        : 'side';
  }

  async loadSchedule() {
    const loadVersion = ++this.scheduleLoadVersion;
    try {
      this.loading = true;
      const scheduleResponse = await firstValueFrom(
        this.dcRosterService.getSchedule(this.buildRequestFilters())
      );
      if (this.destroyed || loadVersion !== this.scheduleLoadVersion) {
        return;
      }
      const totalCount = scheduleResponse.count ?? 0;
      const maxPageIndex = Math.max(
        Math.ceil(totalCount / this.pageSize) - 1,
        0
      );
      if (this.pageIndex > maxPageIndex) {
        this.pageIndex = maxPageIndex;
        await this.loadSchedule();
        return;
      }

      const rows = scheduleResponse.rows ?? [];
      this.totalCount = totalCount;
      this.rows = rows;
      if (this.focusedGameId) {
        if (rows.some((row) => row.gameId === this.focusedGameId)) {
          this.expandedGameIds.add(this.focusedGameId);
          this.focusedGameErrorMessage = '';
        } else {
          this.focusedGameErrorMessage =
            'No Schedule game was found for this Game ID.';
        }
      }
      this.autoAssignGameLimit = scheduleResponse.autoAssignGameLimit ?? null;
      this.setTeamLeaderDirectory(
        this.preserveSelectedTeamLeader(this.buildTeamLeaderDirectory(rows))
      );
      this.dcTypeOptions = scheduleResponse.dcTypes ?? [];
      this.leagueOptions = scheduleResponse.leagues ?? [];
    } catch (error) {
      if (this.destroyed || loadVersion !== this.scheduleLoadVersion) {
        return;
      }
      this.rows = [];
      this.totalCount = 0;
      this.dcTypeOptions = [];
      this.leagueOptions = [];
      this.setTeamLeaderDirectory(
        this.preserveSelectedTeamLeader(new Map<string, string>())
      );
      this.alertService.showError(
        'Could not load Schedule: ' + (error?.message ?? 'unknown error')
      );
    } finally {
      if (loadVersion === this.scheduleLoadVersion) {
        this.loading = false;
      }
    }
  }

  onFiltersChanged() {
    this.pageIndex = 0;
    this.selectedGameIds.clear();
    void this.loadSchedule();
  }

  onDcDateFromChanged() {
    if (
      this.filters.dcDateFrom &&
      this.filters.dcDateTo &&
      this.filters.dcDateFrom > this.filters.dcDateTo
    ) {
      this.filters.dcDateTo = this.buildDateFilterFromDateStringWithDayOffset(
        this.filters.dcDateFrom,
        DEFAULT_DC_DATE_RANGE_DAYS
      );
      this.alertService.showInfo(
        `DC date to was moved to ${this.filters.dcDateTo} to keep the default ${DEFAULT_DC_DATE_RANGE_DAYS}-day range.`
      );
    }
    this.onFiltersChanged();
  }

  onTeamLeaderFilterChanged(teamLeaderAuthId: string) {
    this.filters.teamLeaderAuthId = teamLeaderAuthId ?? '';
    this.onFiltersChanged();
  }

  clearFilters() {
    this.filters = this.createDefaultFilters();
    this.onFiltersChanged();
  }

  onPageChanged(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    void this.loadSchedule();
  }

  toggleSelected(row: DcScheduleRow) {
    if (this.selectedGameIds.has(row.gameId)) {
      this.selectedGameIds.delete(row.gameId);
    } else {
      this.selectedGameIds.add(row.gameId);
    }
  }

  toggleAllSelected() {
    if (this.allFilteredSelected) {
      this.rows.forEach((row) => this.selectedGameIds.delete(row.gameId));
      return;
    }

    this.rows.forEach((row) => this.selectedGameIds.add(row.gameId));
  }

  toggleExpanded(row: DcScheduleRow) {
    if (this.expandedGameIds.has(row.gameId)) {
      this.expandedGameIds.delete(row.gameId);
    } else {
      this.expandedGameIds.add(row.gameId);
    }
  }

  isExpanded(row: DcScheduleRow): boolean {
    return this.expandedGameIds.has(row.gameId);
  }

  isSelected(row: DcScheduleRow): boolean {
    return this.selectedGameIds.has(row.gameId);
  }

  isSavingTeamLeader(row: DcScheduleRow): boolean {
    return this.savingTeamLeaderGameIds.has(row.gameId);
  }

  isMarkingAssigned(row: DcScheduleRow): boolean {
    return this.markingAssignedGameId === row.gameId;
  }

  progressTooltip(row: DcScheduleRow): string {
    return scheduleProgressTooltip(
      row.progressPercentage,
      row.progressBreakdown
    );
  }

  isActiveTeamLeader(auth0Id: string): boolean {
    return this.teamLeaders.some(
      (teamLeader) => teamLeader.auth0Id === auth0Id
    );
  }

  async onTeamLeaderChanged(row: DcScheduleRow, collectorAuthId: string) {
    if (!this.hasWritePermission) {
      return;
    }
    const previousTeamLeaderAuthId = row.teamLeaderAuthId ?? null;
    const nextTeamLeaderAuthId =
      this.normalizeTeamLeaderAuthId(collectorAuthId);
    if (previousTeamLeaderAuthId === nextTeamLeaderAuthId) {
      return;
    }

    try {
      this.savingTeamLeaderGameIds.add(row.gameId);
      this.updateRowTeamLeader(
        row.gameId,
        previousTeamLeaderAuthId,
        nextTeamLeaderAuthId
      );
      const updated = await firstValueFrom(
        this.dcRosterService.updateScheduleTeamLeader(
          row.gameId,
          nextTeamLeaderAuthId
        )
      );
      const confirmedTeamLeaderAuthId = this.normalizeTeamLeaderAuthId(
        updated.collectorAuthId
      );
      this.updateRowTeamLeader(
        row.gameId,
        nextTeamLeaderAuthId,
        confirmedTeamLeaderAuthId
      );
    } catch (error) {
      this.updateRowTeamLeader(
        row.gameId,
        nextTeamLeaderAuthId,
        previousTeamLeaderAuthId
      );
      this.alertService.showError(
        'Could not update Team Leader: ' +
          (await this.formatTeamLeaderUpdateError(error))
      );
    } finally {
      this.savingTeamLeaderGameIds.delete(row.gameId);
    }
  }

  onScheduleDetailsChanged(
    row: DcScheduleRow,
    details: DcScheduleDetails
  ): void {
    this.rows = this.rows.map((currentRow) => {
      if (currentRow.gameId !== row.gameId) {
        return currentRow;
      }
      return {
        ...this.applyRowCoverage(
          {
            ...currentRow,
            teamLeaderAuthId: details.teamLeaderAuthId,
            teamLeaderName: details.teamLeaderAuthId
              ? this.getTeamLeaderName(details.teamLeaderAuthId)
              : null,
            progressPercentage: details.progressPercentage,
            progressBreakdown: details.progressBreakdown
          },
          details.rosterAssigned,
          details.rosterTotal
        )
      };
    });
  }

  onRosterDeleted(gameId: string): void {
    const hadRow = this.rows.some((row) => row.gameId === gameId);
    this.rows = this.rows.filter((row) => row.gameId !== gameId);
    if (hadRow) {
      this.totalCount = Math.max(0, this.totalCount - 1);
    }
    this.selectedGameIds.delete(gameId);
    this.expandedGameIds.delete(gameId);
    if (this.focusedGameId === gameId) {
      this.focusedGameErrorMessage =
        'This game no longer has a Schedule roster.';
    }
  }

  canMarkAsAssigned(row: DcScheduleRow): boolean {
    return (
      row.assignmentStatus === 'ASSIGNED' &&
      this.hasWritePermission &&
      (row.autoAssignStatus !== 'SUCCESS' ||
        (row.failureReasons?.length ?? 0) > 0 ||
        (row.warnings?.length ?? 0) > 0)
    );
  }

  async markAsAssigned(row: DcScheduleRow) {
    if (
      !this.hasWritePermission ||
      this.isMarkingAssigned(row) ||
      !this.canMarkAsAssigned(row)
    ) {
      return;
    }

    try {
      this.markingAssignedGameId = row.gameId;
      const updated = await firstValueFrom(
        this.dcRosterService.markScheduleAssigned(row.gameId)
      );
      this.rows = this.rows.map((currentRow) =>
        currentRow.gameId === row.gameId
          ? {
              ...this.applyRowCoverage(
                currentRow,
                updated.rosterAssigned,
                updated.rosterTotal
              ),
              autoAssignStatus: updated.autoAssignStatus,
              failureReasons: updated.failureReasons ?? [],
              warnings: updated.warnings ?? []
            }
          : currentRow
      );
    } catch (error) {
      this.alertService.showError(
        'Could not mark roster as assigned: ' +
          (await getHttpErrorMessage(error))
      );
    } finally {
      if (this.markingAssignedGameId === row.gameId) {
        this.markingAssignedGameId = null;
      }
    }
  }

  readSelectValue(event: Event): string {
    return (event?.target as HTMLSelectElement | null)?.value ?? '';
  }

  async runAutoAssign() {
    if (!this.hasWritePermission || this.loading || this.running) {
      return;
    }

    if (this.selectedCount > 0) {
      await this.runAutoAssignSelected();
      return;
    }

    await this.runAutoAssignForActiveFilters();
  }

  async runAutoAssignSelected() {
    if (!this.hasWritePermission) {
      return;
    }
    const gameIds = [...this.selectedGameIds];
    if (gameIds.length === 0) {
      return;
    }

    await this.executeAutoAssign(
      this.dcRosterService.runScheduleAutoAssign(gameIds)
    );
  }

  async runAutoAssignForActiveFilters() {
    if (
      !this.hasWritePermission ||
      this.loading ||
      this.running ||
      this.totalCount === 0
    ) {
      return;
    }

    const dialogData = this.buildAutoAssignDialogData();
    const dialogRef = this.dialog.open(ScheduleAutoAssignDialogComponent, {
      autoFocus: false,
      data: dialogData,
      width: '520px'
    });
    const confirmed = await firstValueFrom(dialogRef.afterClosed());
    if (!confirmed || !dialogData.canConfirm) {
      return;
    }

    await this.executeAutoAssign(
      this.dcRosterService.runScheduleAutoAssignForFilters(
        this.buildAutoAssignFilters()
      )
    );
  }

  /**
   * Runs the config, preview, confirmation, and assignment steps against one
   * immutable snapshot of the active schedule filters.
   */
  async openAssignTeamLeaders(): Promise<void> {
    if (
      !this.hasWritePermission ||
      this.loading ||
      this.running ||
      this.totalCount === 0
    ) {
      return;
    }

    const configDialogData: TeamLeaderAssignmentConfigDialogData = {
      leagueOptions: this.leagueOptions,
      teamLeaders: this.teamLeaders
    };
    const configDialogRef = this.dialog.open(
      TeamLeaderAssignmentConfigDialogComponent,
      {
        autoFocus: false,
        data: configDialogData,
        width: '50vw',
        minWidth: 'min(680px, calc(100vw - 32px))',
        maxWidth: 'calc(100vw - 32px)'
      }
    );
    const rows = await firstValueFrom(configDialogRef.afterClosed());
    if (rows == null) {
      return;
    }
    if (rows.length === 0) {
      this.alertService.showInfo(
        'Configuration saved, no coverage rows defined.'
      );
      return;
    }

    const filters = this.buildAutoAssignFilters();
    let preview: TeamLeaderAssignPreview;
    try {
      preview = await firstValueFrom(
        this.dcRosterService.previewTeamLeaderAssign(filters)
      );
    } catch (error) {
      this.alertService.showError(
        'Could not preview Team Leader assignment: ' +
          (await getHttpErrorMessage(error))
      );
      return;
    }

    const confirmData = this.buildTeamLeaderAssignDialogData(rows, preview);
    const confirmDialogRef = this.dialog.open(
      ScheduleTeamLeaderAssignDialogComponent,
      {
        autoFocus: false,
        data: confirmData,
        width: '520px',
        maxWidth: 'calc(100vw - 32px)'
      }
    );
    const confirmed = await firstValueFrom(confirmDialogRef.afterClosed());
    if (!confirmed || !this.canRunTeamLeaderAssign(preview)) {
      return;
    }

    await this.executeTeamLeaderAssign(
      this.dcRosterService.runTeamLeaderAssign(filters)
    );
  }

  formatDateTime(date: string, time: string): string {
    const normalizedDate = date?.trim();
    const normalizedTime = time?.trim();
    if (!normalizedDate) {
      return '-';
    }
    return normalizedTime
      ? `${normalizedDate} ${normalizedTime}`
      : normalizedDate;
  }

  getTeamLeaderName(auth0Id: string | null): string {
    if (!auth0Id || auth0Id === UNASSIGNED_TEAM_LEADER_FILTER_VALUE) {
      return 'Unassigned';
    }
    return (
      this.teamLeaderDirectory.get(auth0Id) ??
      this.teamLeaders.find((teamLeader) => teamLeader.auth0Id === auth0Id)
        ?.name ??
      auth0Id
    );
  }

  getAssignmentStatusClass(status: ScheduleAssignmentStatus): string {
    return `status-${status.toLowerCase().replace(/ /g, '-')}`;
  }

  getAutoAssignStatusClass(status: ScheduleAutoAssignStatus): string {
    return `status-${status.toLowerCase().replace(/ /g, '-')}`;
  }

  getGameStatusLabel(status: GameStatus): string {
    return status
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  trackByGameId(_index: number, row: DcScheduleRow): string {
    return row.gameId;
  }

  private async loadTeamLeaders() {
    try {
      const response = await firstValueFrom(
        this.dcRosterService.getScheduleTeamLeaders()
      );
      if (this.destroyed) {
        return;
      }
      this.setTeamLeaders(response.teamLeaders ?? []);
    } catch (error) {
      if (this.destroyed) {
        return;
      }
      this.setTeamLeaders([]);
      this.alertService.showError(
        'Could not load Team Leaders: ' + (await getHttpErrorMessage(error))
      );
    }
  }

  private async loadWritePermission(): Promise<void> {
    try {
      this.hasWritePermission = await firstValueFrom(
        this.authService.hasPermission('write:dc-roster')
      );
    } catch (_error) {
      this.hasWritePermission = false;
    }
  }

  private buildTeamLeaderDirectory(rows: DcScheduleRow[]): Map<string, string> {
    return rows.reduce((directory, row) => {
      const auth0Id = row.teamLeaderAuthId?.trim();
      const name = row.teamLeaderName?.trim();
      if (auth0Id && name) {
        directory.set(auth0Id, name);
      }
      return directory;
    }, new Map<string, string>());
  }

  private setTeamLeaders(teamLeaders: TeamLeaderOption[]): void {
    this.teamLeaders = teamLeaders;
    this.recomputeInactiveAssignedTeamLeaders();
  }

  private setTeamLeaderDirectory(directory: Map<string, string>): void {
    this.teamLeaderDirectory = directory;
    this.recomputeInactiveAssignedTeamLeaders();
  }

  private recomputeInactiveAssignedTeamLeaders(): void {
    const activeTeamLeaderIds = new Set(
      this.teamLeaders.map((teamLeader) => teamLeader.auth0Id)
    );
    this.inactiveAssignedTeamLeaders = [...this.teamLeaderDirectory.entries()]
      .filter(([auth0Id]) => !activeTeamLeaderIds.has(auth0Id))
      .map(([auth0Id, name]) => ({ auth0Id, name }))
      .sort(
        (left, right) =>
          left.name.localeCompare(right.name) ||
          left.auth0Id.localeCompare(right.auth0Id)
      );
  }

  private preserveSelectedTeamLeader(
    directory: Map<string, string>
  ): Map<string, string> {
    const selectedAuth0Id = this.filters.teamLeaderAuthId?.trim();
    if (
      !selectedAuth0Id ||
      selectedAuth0Id === UNASSIGNED_TEAM_LEADER_FILTER_VALUE ||
      directory.has(selectedAuth0Id)
    ) {
      return directory;
    }

    const selectedName = this.teamLeaderDirectory.get(selectedAuth0Id);
    if (selectedName) {
      directory.set(selectedAuth0Id, selectedName);
    }
    return directory;
  }

  private buildRequestFilters(): DcScheduleFilters {
    if (this.focusedGameId) {
      return {
        gameId: this.focusedGameId,
        page: 0,
        pageSize: 1
      };
    }
    return {
      ...this.buildAutoAssignFilters(),
      page: this.pageIndex,
      pageSize: this.pageSize
    };
  }

  private buildAutoAssignFilters(): DcScheduleAutoAssignFilters {
    const teamLeaderUnassigned =
      this.filters.teamLeaderAuthId === UNASSIGNED_TEAM_LEADER_FILTER_VALUE;
    return {
      dcType: this.filters.dcType,
      league: this.filters.league,
      gameDate: this.filters.gameDate,
      dcDateFrom: this.filters.dcDateFrom,
      dcDateTo: this.filters.dcDateTo,
      teamLeaderAuthId: teamLeaderUnassigned
        ? ''
        : this.filters.teamLeaderAuthId,
      ...(teamLeaderUnassigned ? { teamLeaderUnassigned: true } : {}),
      gameStatus: this.filters.gameStatus || null
    };
  }

  private createDefaultFilters(): ScheduleFilters {
    return {
      ...EMPTY_FILTERS,
      dcDateFrom: this.buildDateFilterWithDayOffset(0),
      dcDateTo: this.buildDateFilterWithDayOffset(DEFAULT_DC_DATE_RANGE_DAYS)
    };
  }

  private async loadFocusedGame(gameId: string): Promise<void> {
    this.scheduleLoadVersion += 1;
    this.loading = false;
    this.rows = [];
    this.totalCount = 0;
    this.pageIndex = 0;
    this.selectedGameIds.clear();
    this.expandedGameIds.clear();
    this.focusedGameId = null;
    this.focusedGameErrorMessage = '';
    this.requestedGameId = gameId || null;
    if (!gameId) {
      await this.loadSchedule();
      return;
    }
    if (!OBJECT_ID_HEX_PATTERN.test(gameId)) {
      this.focusedGameErrorMessage =
        'Invalid Game ID. Expected a 24-character ObjectID.';
      return;
    }
    this.focusedGameId = gameId.toLowerCase();
    await this.loadSchedule();
  }

  private buildDateFilterWithDayOffset(dayOffset: number): string {
    return DateTime.now()
      .setZone(TASKS_SOURCE_TIME_ZONE)
      .plus({ days: dayOffset })
      .toISODate();
  }

  private buildDateFilterFromDateStringWithDayOffset(
    dateString: string,
    dayOffset: number
  ): string {
    const date = DateTime.fromISO(dateString, {
      zone: TASKS_SOURCE_TIME_ZONE
    });
    return date.isValid
      ? date.plus({ days: dayOffset }).toISODate()
      : dateString;
  }

  private normalizeTeamLeaderAuthId(
    collectorAuthId: string | null | undefined
  ): string | null {
    return collectorAuthId?.trim() || null;
  }

  private updateRowTeamLeader(
    gameId: string,
    previousTeamLeaderAuthId: string | null,
    teamLeaderAuthId: string | null
  ): void {
    this.rows = this.rows.map((row) =>
      row.gameId === gameId
        ? this.applyRowCoverage(
            {
              ...row,
              teamLeaderAuthId,
              teamLeaderName: teamLeaderAuthId
                ? this.getTeamLeaderName(teamLeaderAuthId)
                : null
            },
            this.getNextAssignedCountForTeamLeaderChange(
              row,
              previousTeamLeaderAuthId,
              teamLeaderAuthId
            ),
            row.rosterTotal
          )
        : row
    );
  }

  private applyRowCoverage(
    row: DcScheduleRow,
    rosterAssigned: number,
    rosterTotal: number
  ): DcScheduleRow {
    const total = Math.max(rosterTotal, 0);
    const assigned = Math.min(Math.max(rosterAssigned, 0), total);
    const assignmentStatus = deriveScheduleAssignmentStatus(assigned, total);

    return {
      ...row,
      rosterTotal: total,
      rosterAssigned: assigned,
      assignmentStatus,
      autoAssignStatus: deriveEffectiveScheduleAutoAssignStatus(
        row.autoAssignStatus,
        assignmentStatus
      )
    };
  }

  private getNextAssignedCountForTeamLeaderChange(
    row: DcScheduleRow,
    previousTeamLeaderAuthId: string | null,
    nextTeamLeaderAuthId: string | null
  ): number {
    const hadPreviousTeamLeader = !!previousTeamLeaderAuthId?.trim();
    const hasNextTeamLeader = !!nextTeamLeaderAuthId?.trim();

    if (!hadPreviousTeamLeader && hasNextTeamLeader) {
      return row.rosterAssigned + 1;
    }
    if (hadPreviousTeamLeader && !hasNextTeamLeader) {
      return row.rosterAssigned - 1;
    }
    return row.rosterAssigned;
  }

  private async formatTeamLeaderUpdateError(error: any): Promise<string> {
    if (error?.error?.code === MISSING_GM_EVENT_ERROR_CODE) {
      return MISSING_GM_EVENT_TEAM_LEADER_MESSAGE;
    }
    return getHttpErrorMessage(error);
  }

  private buildAutoAssignDialogData(): ScheduleAutoAssignDialogData {
    const gameCount = this.totalCount;
    const countExceedsLimit =
      this.autoAssignGameLimit != null && gameCount > this.autoAssignGameLimit;

    return {
      gameCount,
      filterItems: this.buildAutoAssignDialogItems(),
      canConfirm: gameCount > 0 && !countExceedsLimit,
      warningMessage: countExceedsLimit
        ? `Auto-assign can run for up to ${this.autoAssignGameLimit} games at once. Narrow the filters and try again.`
        : null
    };
  }

  private buildAutoAssignDialogItems(): ScheduleAutoAssignDialogItem[] {
    const items: ScheduleAutoAssignDialogItem[] = [];
    this.pushDialogItem(items, 'DC Type', this.filters.dcType);
    this.pushDialogItem(items, 'League', this.filters.league);
    this.pushDialogItem(items, 'Game Date', this.filters.gameDate);
    this.pushDialogItem(items, 'DC Date', this.formatDcDateRangeSummary());
    this.pushDialogItem(
      items,
      'Team Leader',
      this.filters.teamLeaderAuthId
        ? this.getTeamLeaderName(this.filters.teamLeaderAuthId)
        : ''
    );
    this.pushDialogItem(
      items,
      'Game Status',
      this.filters.gameStatus
        ? this.getGameStatusLabel(this.filters.gameStatus)
        : ''
    );
    return items;
  }

  private buildTeamLeaderAssignDialogData(
    rows: TeamLeaderCoverageRow[],
    preview: TeamLeaderAssignPreview
  ): ScheduleTeamLeaderAssignDialogData {
    return {
      gameCount: preview.gameCount,
      autoAssignGameLimit: preview.autoAssignGameLimit,
      filterItems: this.buildAutoAssignDialogItems(),
      coveragePlan: this.buildTeamLeaderCoveragePlan(rows, preview),
      validationErrors: preview.validationErrors ?? []
    };
  }

  private buildTeamLeaderCoveragePlan(
    rows: TeamLeaderCoverageRow[],
    preview: TeamLeaderAssignPreview
  ): ScheduleTeamLeaderCoveragePlanItem[] {
    const inScopeLeagueSihfIds = new Set(
      preview.leagues.map((league) => league.leagueSihfId)
    );
    return rows
      .filter(
        (row) =>
          row.leagueSihfId != null && inScopeLeagueSihfIds.has(row.leagueSihfId)
      )
      .map((row) => ({
        leagueShortName: row.leagueShortName,
        teamLeaderName: this.getTeamLeaderName(row.teamLeaderAuthId),
        coverage: Number(row.coverage) || 0
      }));
  }

  private canRunTeamLeaderAssign(preview: TeamLeaderAssignPreview): boolean {
    return (
      preview.gameCount > 0 &&
      preview.gameCount <= preview.autoAssignGameLimit &&
      (preview.validationErrors?.length ?? 0) === 0
    );
  }

  private pushDialogItem(
    items: ScheduleAutoAssignDialogItem[],
    label: string,
    value: string | null | undefined
  ): void {
    const normalizedValue = value?.trim();
    if (!normalizedValue) {
      return;
    }
    items.push({ label, value: normalizedValue });
  }

  private formatDcDateRangeSummary(): string {
    const from = this.filters.dcDateFrom?.trim() ?? '';
    const to = this.filters.dcDateTo?.trim() ?? '';

    if (from && to) {
      return `${from} to ${to}`;
    }
    if (from) {
      return `From ${from}`;
    }
    if (to) {
      return `Up to ${to}`;
    }
    return '';
  }

  private async executeAutoAssign(
    request$: Observable<AutoAssignResponse>
  ): Promise<void> {
    try {
      this.running = true;
      const response = await firstValueFrom(request$);
      this.selectedGameIds.clear();
      await this.loadSchedule();
      this.dialog.open(ScheduleAutoAssignResultDialogComponent, {
        autoFocus: false,
        data: { response },
        width: '480px',
        maxWidth: 'calc(100vw - 32px)'
      });
    } catch (error) {
      this.alertService.showError(
        'Could not run auto-assign: ' + (await getHttpErrorMessage(error))
      );
    } finally {
      this.running = false;
    }
  }

  /**
   * Executes a confirmed Team Leader assignment, reloads the schedule, and
   * reports the guarded-write summary.
   */
  private async executeTeamLeaderAssign(
    request$: Observable<TeamLeaderAssignResult>
  ): Promise<void> {
    try {
      this.running = true;
      const result = await firstValueFrom(request$);
      await this.loadSchedule();
      this.showTeamLeaderAssignResult(result);
    } catch (error) {
      this.alertService.showError(
        'Could not assign Team Leaders: ' + (await getHttpErrorMessage(error))
      );
    } finally {
      this.running = false;
    }
  }

  private showTeamLeaderAssignResult(result: TeamLeaderAssignResult): void {
    const message =
      `Team Leader assignment completed: processed ${result.processed}, ` +
      `assigned ${result.assigned}, skipped ${result.skippedAlreadyAssigned} already assigned, ` +
      `${result.skippedMissingGmEvent} missing GM event.`;
    if ((result.errors?.length ?? 0) > 0) {
      this.alertService.showWarning(`${message} ${result.errors.join(' ')}`);
      return;
    }
    this.alertService.showInfo(message);
  }
}
